import React, { useCallback } from "react";
import styled from "styled-components";

import { reactKeyFromIDSK } from "@examspark/common";
import { DisplayName } from "@examspark/common-app";
import { VSpace } from "@examspark/common-ui";
import {
  CardAction,
  CardLink,
  LinkCard,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import { Maybe, Resource } from "@examspark/types";

import { getBrowseForItem } from "../../utils";
import BankBadge from "../common/BankBadge";
import FavoriteButton from "../common/FavoriteButton";
import magnifyingGlass from "../common/graphics/magnifying-glass.svg";

import ItemMetadata from "./ItemMetadata";

const Results = styled(VSpace)`
  gap: 0.6rem;
`;

export const EmptyBox = styled.div`
  border: dashed 2px #ddd;
  border-radius: 0.5rem;
  padding: 5rem 3rem;
  align-items: center;
  justify-content: center;
  display: flex;

  flex-direction: column;
  gap: 2rem;

  h1 {
    font-size: 3rem;
    font-weight: 300;
    color: #333;
    margin: 0;
  }
  svg {
    fill: #888;
    width: 6.4rem;
  }
`;

const ResultTitle = styled.h3`
  color: #6450c2;
  font-weight: inherit;
  font-size: inherit;
`;

const Result = styled(LinkCard)`
  padding: 1rem 1rem;
  color: #333;
  gap: 1rem;
  & ${CardLink}:hover, & ${CardLink}:focus-visible {
    text-decoration: underline;
  }
`;

const ResultTop = styled.header`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const ResultMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ResultType = styled.div`
  color: #888;
`;

export const getSearchResultName = (item: Resource) => {
  if (item.sk === "course-verification#v0") {
    return `${item.name} TVD`;
  }
  if (item.sk === "course-plan#v0") {
    return `${item.name} TEKS YAG`;
  }

  return item.name;
};

const NoResultsText = styled.span`
  font-size: 3rem;
`;

// this should be basically the "Content" GQL base type (which isn't available in TS for some reason)
type BrowseListRequiredFields = Pick<
  Resource,
  | "id"
  | "sk"
  | "workspaceId"
  | "schemaVersion"
  | "name"
  | "bankId"
  | "subject"
  | "grade"
  | "updatedAt"
>;

type BrowseListProps<T extends BrowseListRequiredFields> = {
  data?: Array<Maybe<T>>;
  query?: string;
  noResultsPlaceholder?: string;
  hideBankBadge?: boolean;
  onResultSelect?: (item: T) => void;
  onResultNavigate?: () => void;
  itemMetadata?: (item: T) => React.ReactNode;
};

const BrowseList = <T extends BrowseListRequiredFields>({
  data,
  query,
  noResultsPlaceholder,
  hideBankBadge,
  onResultSelect,
  onResultNavigate,
  itemMetadata,
}: BrowseListProps<T>) => {
  const onNavigateClick = useCallback(
    (e: React.MouseEvent, item: T) => {
      if (onResultSelect != null) {
        e.preventDefault();
        onResultSelect(item);
      }

      onResultNavigate?.();
    },
    [onResultSelect, onResultNavigate],
  );

  return (
    <Results>
      {!data || !data.length ? (
        <EmptyBox>
          <img alt="Searching" src={magnifyingGlass} width="64" />
          <NoResultsText>
            {noResultsPlaceholder ?? "No search results."}
          </NoResultsText>
        </EmptyBox>
      ) : (
        data.map((item: Maybe<T>, index) => {
          if (!item) return null;
          if (
            query != null &&
            !item.name?.toLowerCase().includes(query.toLowerCase())
          )
            return null;
          const { label, url } = getBrowseForItem(item);
          const name = getSearchResultName(item);

          return (
            <Result key={reactKeyFromIDSK(item)} clickable={url != null}>
              <ResultTop>
                <ResultMain>
                  <ResultTitle>
                    {url == null ? (
                      name
                    ) : (
                      <CardLink
                        to={url}
                        onClick={(e) => onNavigateClick(e, item)}
                      >
                        <DisplayName resource={item} />
                      </CardLink>
                    )}
                  </ResultTitle>
                  <CardAction style={{ order: -1 }}>
                    <FavoriteButton item={item} />
                  </CardAction>
                </ResultMain>

                <ResultType>{label}</ResultType>
                {!hideBankBadge && <BankBadge bankId={item.bankId} />}
              </ResultTop>
              {itemMetadata ? itemMetadata(item) : <ItemMetadata item={item} />}
            </Result>
          );
        })
      )}
    </Results>
  );
};

export default BrowseList;
